import React from 'react';
import PropTypes from 'prop-types';

import Thumbnail from '../Thumbnail';
import ThumbnailNoImage from '../ThumbnailNoImage';
import ThumbnailTracked from '../ThumbnailTracked';
import * as Types from '../../types';

const ThumbnailList = ({
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
}) => {
  const renderThumbnail = ({
    displaySetInstanceUID,
    description,
    dragData,
    seriesNumber,
    numInstances,
    modality,
    componentType,
    seriesDate,
    countIcon,
    viewportIdentificator,
    isTracked,
    canReject,
    onReject,
    imageSrc,
    messages,
    imageAltText,
    isHydratedForDerivedDisplaySet,
  }) => {
    const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
    switch (componentType) {
      case 'thumbnail':
        return (
          <Thumbnail
            key={displaySetInstanceUID}
            displaySetInstanceUID={displaySetInstanceUID}
            dragData={dragData}
            description={description}
            seriesNumber={seriesNumber}
            numInstances={numInstances}
            countIcon={countIcon}
            imageSrc={imageSrc}
            imageAltText={imageAltText}
            messages={messages}
            viewportIdentificator={viewportIdentificator}
            isActive={isActive}
            onClick={() => onThumbnailClick(displaySetInstanceUID)}
            onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
          />
        );
      case 'thumbnailTracked':
        return (
          <ThumbnailTracked
            key={displaySetInstanceUID}
            displaySetInstanceUID={displaySetInstanceUID}
            dragData={dragData}
            description={description}
            seriesNumber={seriesNumber}
            numInstances={numInstances}
            countIcon={countIcon}
            imageSrc={imageSrc}
            imageAltText={imageAltText}
            messages={messages}
            viewportIdentificator={viewportIdentificator}
            isTracked={isTracked}
            isActive={isActive}
            onClick={() => onThumbnailClick(displaySetInstanceUID)}
            onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
            onClickUntrack={() => onClickUntrack(displaySetInstanceUID)}
          />
        );
      case 'thumbnailNoImage':
        return (
          <ThumbnailNoImage
            isActive={isActive}
            key={displaySetInstanceUID}
            displaySetInstanceUID={displaySetInstanceUID}
            dragData={dragData}
            modality={modality}
            modalityTooltip={_getModalityTooltip(modality)}
            messages={messages}
            seriesDate={seriesDate}
            description={description}
            canReject={canReject}
            onReject={onReject}
            onClick={() => onThumbnailClick(displaySetInstanceUID)}
            onDoubleClick={() => onThumbnailDoubleClick(displaySetInstanceUID)}
            viewportIdentificator={viewportIdentificator}
            isHydratedForDerivedDisplaySet={isHydratedForDerivedDisplaySet}
          />
        );
      default:
        return <></>;
    }
  };

  // Group an enhanced (Focus Filter) shot together with the original it was
  // derived from. The panel orders each enhanced thumbnail right after its
  // original, so we attach any consecutive enhanced thumbnails to the most
  // recent original and render the pair inside one bordered card.
  const groups = [];
  thumbnails.forEach(thumbnail => {
    if (thumbnail.isEnhanced && groups.length > 0) {
      groups[groups.length - 1].push(thumbnail);
    } else {
      groups.push([thumbnail]);
    }
  });

  return (
    <div
      id="ohif-thumbnail-list"
      className="ohif-scrollbar study-min-height bg-primary-dark overflow-y-hidden py-3"
    >
      {groups.map(group => {
        const groupKey = group[0].displaySetInstanceUID;

        // No enhanced child — render the thumbnail as-is.
        if (group.length === 1) {
          return <React.Fragment key={groupKey}>{renderThumbnail(group[0])}</React.Fragment>;
        }

        // Original + its enhanced copy/copies — wrap them in a single card so
        // the relationship is visually obvious.
        return (
          <div
            key={groupKey}
            className="mx-2 mb-2 rounded-md pt-1"
            style={{
              border: '1px solid rgba(66, 133, 244, 0.45)',
              backgroundColor: 'rgba(66, 133, 244, 0.06)',
            }}
          >
            {group.map((thumbnail, index) => (
              <div
                key={thumbnail.displaySetInstanceUID}
                style={{
                  // Pull enhanced rows up under their original and trim the
                  // trailing thumbnail margin so the card hugs its contents.
                  marginTop: thumbnail.isEnhanced ? '-1.25rem' : undefined,
                  marginBottom: index === group.length - 1 ? '-1.25rem' : undefined,
                }}
              >
                {renderThumbnail(thumbnail)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

ThumbnailList.propTypes = {
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      displaySetInstanceUID: PropTypes.string.isRequired,
      imageSrc: PropTypes.string,
      imageAltText: PropTypes.string,
      seriesDate: PropTypes.string,
      seriesNumber: Types.StringNumber,
      numInstances: PropTypes.number,
      description: PropTypes.string,
      componentType: Types.ThumbnailType.isRequired,
      viewportIdentificator: Types.StringArray,
      isTracked: PropTypes.bool,
      /**
       * Data the thumbnail should expose to a receiving drop target. Use a matching
       * `dragData.type` to identify which targets can receive this draggable item.
       * If this is not set, drag-n-drop will be disabled for this thumbnail.
       *
       * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
       */
      dragData: PropTypes.shape({
        /** Must match the "type" a dropTarget expects */
        type: PropTypes.string.isRequired,
      }),
    })
  ),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  onThumbnailClick: PropTypes.func.isRequired,
  onThumbnailDoubleClick: PropTypes.func.isRequired,
  onClickUntrack: PropTypes.func.isRequired,
};

// TODO: Support "Viewport Identificator"?
function _getModalityTooltip(modality) {
  if (_modalityTooltips.hasOwnProperty(modality)) {
    return _modalityTooltips[modality];
  }

  return 'Unknown';
}

const _modalityTooltips = {
  SR: 'Structured Report',
  SEG: 'Segmentation',
  RTSTRUCT: 'RT Structure Set',
};

export default ThumbnailList;
